import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Sparkles, Gamepad2, ChevronRight, X, Target } from "lucide-react";
import { useApp, API } from "../context";

const FirstMissionPrompt = () => {
  const { user } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [visible, setVisible] = useState(false);

  // Account-based first-login prompt: shows ONCE per account (server flag),
  // not per browser/device. Never re-shows after it's been seen.
  useEffect(() => {
    if (location.pathname !== "/" || !user || user.isDemo) return;
    if (user.role === "child_explorer") return;         // Kids get KidOnboarding
    if (user.support_viewing) return;                   // not during a support session
    if (sessionStorage.getItem("isaiah_kid_onboarding_active")) return;
    if (sessionStorage.getItem("isaiah_just_signed_up")) return;  // welcome modal handles it
    if (sessionStorage.getItem("isaiah_first_mission_seen")) return;

    let cancelled = false;
    let timer;
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/first-mission-status`);
        if (cancelled || r.data?.seen) return;
        timer = setTimeout(() => { if (!cancelled) setVisible(true); }, 2500);
      } catch { /* if the check fails, don't nag */ }
    })();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [user, location.pathname]);

  const dismiss = () => {
    setVisible(false);
    sessionStorage.setItem("isaiah_first_mission_seen", "1");
    axios.post(`${API}/auth/first-mission-status`).catch(() => {});
  };

  const startMission = (path) => {
    dismiss();
    navigate(path);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[9990] w-full max-w-sm px-4" data-testid="first-mission-prompt"
      style={{ animation: "section-rise 0.5s ease-out" }}>
      <div className="rounded-2xl border-2 border-amber-400/25 bg-slate-900/95 shadow-2xl overflow-hidden"
        style={{ boxShadow: "0 8px 40px rgba(251,191,36,0.12), 0 0 0 1px rgba(251,191,36,0.05)" }}>

        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-3 pb-1">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-amber-500/15 border border-amber-400/25">
              <Target size={14} className="text-amber-400" />
            </div>
            <div>
              <p className="text-amber-300 text-[10px] font-bold uppercase tracking-wider">Your First Mission</p>
            </div>
          </div>
          <button onClick={dismiss} className="text-white/25 hover:text-white/50 transition-colors p-1" data-testid="first-mission-close">
            <X size={14} />
          </button>
        </div>

        {/* Mission */}
        <div className="px-4 pb-3">
          <p className="text-white/70 text-xs font-nunito mb-3 leading-relaxed">
            Complete your first activity to earn Stars and start leveling up!
          </p>
          <div className="space-y-1.5">
            <button onClick={() => startMission("/arcade")}
              className="w-full flex items-center gap-3 p-2.5 rounded-xl bg-purple-500/10 border border-purple-400/20 hover:bg-purple-500/15 hover:border-purple-400/30 transition-all group"
              data-testid="mission-play-game">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-purple-500/20">
                <Gamepad2 size={15} className="text-purple-400" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-white text-xs font-fredoka font-bold">Play a Quick Game</p>
                <p className="text-white/35 text-[10px]">Earn 5+ Stars in Arcade City</p>
              </div>
              <ChevronRight size={12} className="text-white/20 group-hover:text-white/40" />
            </button>
            <button onClick={() => startMission("/learn")}
              className="w-full flex items-center gap-3 p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-400/20 hover:bg-emerald-500/15 hover:border-emerald-400/30 transition-all group"
              data-testid="mission-start-lesson">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-emerald-500/20">
                <Sparkles size={15} className="text-emerald-400" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-white text-xs font-fredoka font-bold">Try a Lesson</p>
                <p className="text-white/35 text-[10px]">Earn 10+ Stars — Explore the Forest (free)</p>
              </div>
              <ChevronRight size={12} className="text-white/20 group-hover:text-white/40" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirstMissionPrompt;
